import axios from "axios";
import client from "./client";
import { API_BASE_URL } from "@/lib/constants";
import type { PaginatedResponse } from "@/types/common";
import type {
  AnonymousStatusView,
  Complaint,
  ComplaintCategory,
  ComplaintCategoryBreakdown,
  ComplaintComment,
  ComplaintCreateResponse,
  ComplaintDismissPayload,
  ComplaintEvent,
  ComplaintEvidence,
  ComplaintResolvePayload,
  ComplaintSeverity,
  ComplaintStatus,
  ComplaintTriagePayload,
  ComplaintWatcher,
} from "@/types/complaint";

// Public, unauthenticated client for the anonymous status endpoint.
// We don't want the request interceptor attaching an Authorization header.
const publicClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

export async function listComplaints(
  page = 1,
  perPage = 20,
  status?: ComplaintStatus,
  category?: ComplaintCategory,
  severity?: ComplaintSeverity,
): Promise<PaginatedResponse<Complaint>> {
  const params: Record<string, string | number> = { page, per_page: perPage };
  if (status) params.status = status;
  if (category) params.category = category;
  if (severity) params.severity = severity;
  const { data } = await client.get("/complaints", { params });
  return data;
}

export async function getComplaint(id: string): Promise<Complaint> {
  const { data } = await client.get(`/complaints/${id}`);
  return data;
}

export async function submitComplaint(payload: {
  title: string;
  description: string;
  category: ComplaintCategory;
  severity?: ComplaintSeverity;
  is_anonymous?: boolean;
}): Promise<ComplaintCreateResponse> {
  const { data } = await client.post("/complaints", payload);
  return data;
}

export async function updateComplaint(
  id: string,
  updates: {
    status?: ComplaintStatus;
    severity?: ComplaintSeverity;
    assigned_to?: string;
    resolution_notes?: string;
  },
) {
  const { data } = await client.patch(`/complaints/${id}`, updates);
  return data;
}

export async function getComplaintStats(): Promise<{
  total: number;
  open: number;
  by_status: Record<string, number>;
  by_severity: Record<string, number>;
}> {
  const { data } = await client.get("/complaints/stats");
  return data;
}

export async function triage(
  id: string,
  payload: ComplaintTriagePayload,
): Promise<{ message: string; status: ComplaintStatus; triaged_at: string }> {
  const { data } = await client.post(`/complaints/${id}/triage`, payload);
  return data;
}

export async function assignComplaint(
  id: string,
  assignedTo: string,
): Promise<{ message: string; assigned_to: string }> {
  const { data } = await client.post(`/complaints/${id}/assign`, {
    assigned_to: assignedTo,
  });
  return data;
}

export async function resolveComplaint(
  id: string,
  payload: ComplaintResolvePayload,
): Promise<{ message: string; status: ComplaintStatus; resolved_at: string }> {
  const { data } = await client.post(`/complaints/${id}/resolve`, payload);
  return data;
}

export async function dismissComplaint(
  id: string,
  payload: ComplaintDismissPayload,
): Promise<{ message: string; status: ComplaintStatus; dismissed_at: string }> {
  const { data } = await client.post(`/complaints/${id}/dismiss`, payload);
  return data;
}

export async function listEvents(id: string): Promise<ComplaintEvent[]> {
  const { data } = await client.get(`/complaints/${id}/events`);
  return data;
}

export async function listComments(id: string): Promise<ComplaintComment[]> {
  const { data } = await client.get(`/complaints/${id}/comments`);
  return data;
}

export async function addComment(
  id: string,
  body: string,
  isInternal: boolean,
): Promise<ComplaintComment> {
  const { data } = await client.post(`/complaints/${id}/comments`, {
    body,
    is_internal: isInternal,
  });
  return data;
}

export async function listWatchers(id: string): Promise<ComplaintWatcher[]> {
  const { data } = await client.get(`/complaints/${id}/watchers`);
  return data;
}

export async function addWatcher(
  id: string,
  userId: string,
): Promise<ComplaintWatcher> {
  const { data } = await client.post(`/complaints/${id}/watchers`, {
    user_id: userId,
  });
  return data;
}

export async function removeWatcher(id: string, userId: string): Promise<void> {
  await client.delete(`/complaints/${id}/watchers/${userId}`);
}

export async function listEvidence(id: string): Promise<ComplaintEvidence[]> {
  const { data } = await client.get(`/complaints/${id}/evidence`);
  return data;
}

export async function uploadEvidence(
  id: string,
  file: File,
  isSensitive: boolean,
): Promise<ComplaintEvidence> {
  const form = new FormData();
  form.append("file", file);
  form.append("is_sensitive", String(isSensitive));
  const { data } = await client.post(`/complaints/${id}/evidence`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function listOverdueComplaints(): Promise<Complaint[]> {
  const { data } = await client.get(`/complaints/overdue`);
  return data;
}

export async function getCategoryBreakdown(
  since?: string,
): Promise<ComplaintCategoryBreakdown[]> {
  const params: Record<string, string> = {};
  if (since) params.since = since;
  const { data } = await client.get(`/complaints/category-breakdown`, {
    params,
  });
  return data;
}

export async function getAnonymousStatus(
  orgSlug: string,
  token: string,
): Promise<AnonymousStatusView> {
  const { data } = await publicClient.get(
    `/complaints/anon-status/${orgSlug}/${token}`,
  );
  return data;
}
